/**
 * FAQ chiffrée et métadonnées des pages trajet, CALCULÉES depuis les prix.
 *
 * POURQUOI. Search Console, 90 jours au 28/09/2026 : les recherches de
 * transfert (« vtc cannes aeroport nice » 212 impressions, « transfert privé
 * nice cannes » 82, Nice ⇄ Saint-Tropez ~150…) tombent sur nos pages trajet
 * en position 30 à 60. Sur « vtc aéroport nice cannes », Google n'affiche
 * AUCUN pack local : c'est de l'organique pur, donc gagnable. Et les
 * questions qu'il met en avant sont « combien coûte un transfert » et
 * « comment prendre un VTC à l'aéroport de Nice ».
 *
 * Or nos pages répondaient « voir le tableau » à la question du prix, et leur
 * titre ne portait pas de prix. Le prix réel existe (infos.ts) : il est
 * maintenant écrit en toutes lettres, dans la FAQ (visible ET en JSON-LD,
 * les deux lisent `localizedRouteContent`) et dans le titre.
 *
 * ⛔ RIEN N'EST INVENTÉ. Prix, passagers et bagages viennent de
 * `VEHICLES[].fixedTransfers` / `maxPassengers` / `maxLuggage` (infos.ts),
 * passés en argument. La phrase sur l'ajustement reprend `ratesFootnote`
 * (transferUi.ts), pour que la FAQ ne contredise pas la note du tableau.
 * Les zones VTC de l'aéroport viennent du règlement publié par l'aéroport
 * Nice Côte d'Azur (entreprises.nice.aeroport.fr, « Taxis et VTC » et
 * « Conditions d'accès », relevé le 01/10/2026).
 *
 * Pas d'import de `./index` : ce module est pur, `index.ts` l'appelle.
 */
import type { Locale } from "@/config/i18n";
import type { TransferFaqItem } from "./routes";
import type { TransferPlace } from "./places";

/** Ce dont le générateur a besoin d'un prix (sous-ensemble de TransferPrice). */
export type GeneratedPriceRow = {
  classLabel: string;
  price: number;
  maxPassengers: number;
  maxLuggage: number;
};

type Ctx = {
  from: string;
  to: string;
  airport: boolean;
  durationMin: number;
  rows: GeneratedPriceRow[];
};

const MAX_TITLE = 60;
const MAX_DESCRIPTION = 160;
const len = (s: string): number => [...s].length;

/** Premier candidat qui tient dans la borne, sinon le plus court. */
const fit = (candidates: string[], max: number): string =>
  candidates.find((c) => len(c) <= max) ??
  [...candidates].sort((a, b) => len(a) - len(b))[0];

const euro = (price: number, locale: Locale): string =>
  locale === "en" ? `€${price}` : `${price} €`;

/** « a, b ou c » : virgules, puis la conjonction avant le dernier élément. */
const listJoin = (items: string[], sep: string, last: string): string =>
  items.length <= 1
    ? (items[0] ?? "")
    : `${items.slice(0, -1).join(sep)}${last}${items[items.length - 1]}`;

/** Regroupe les classes de même capacité, de la plus petite à la plus grande. */
const byCapacity = (rows: GeneratedPriceRow[]) => {
  const groups = new Map<string, GeneratedPriceRow[]>();
  for (const row of rows) {
    const key = `${row.maxPassengers}/${row.maxLuggage}`;
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  return [...groups.values()].sort(
    (a, b) => a[0].maxPassengers - b[0].maxPassengers,
  );
};

// ─── Textes par langue ──────────────────────────────────────────────────────

const TEXT = {
  fr: {
    priceQ: (c: Ctx) => `Combien coûte un VTC ${c.from} ⇄ ${c.to} ?`,
    priceA: (c: Ctx) =>
      `Le prix est fixé à la réservation, par véhicule : ${c.rows
        .map(
          (r) =>
            `${euro(r.price, "fr")} en ${r.classLabel} (${r.maxPassengers} passagers)`,
        )
        .join(
          ", ",
        )}. Le trafic ne le change pas. Seules des options demandées (attente, arrêt en route, bagages hors norme) peuvent l'ajuster, et le prix final vous est confirmé par écrit avant la prestation.`,
    vehicleQ: (c: Ctx) =>
      `Quel véhicule choisir pour ${c.from} ⇄ ${c.to} selon le nombre de passagers ?`,
    vehicleA: (c: Ctx) =>
      `${byCapacity(c.rows)
        .map(
          (g) =>
            `Jusqu'à ${g[0].maxPassengers} passagers et ${g[0].maxLuggage} bagages : ${listJoin(
              g.map((r) => `${r.classLabel} à ${euro(r.price, "fr")}`),
              ", ",
              " ou ",
            )}`,
        )
        .join(
          ". ",
        )}. Pour un groupe plus grand, un Sprinter VIP de 16 places est proposé sur demande.`,
    airportQ: "Où retrouver son chauffeur VTC à l'aéroport de Nice ?",
    airportA:
      "Le chauffeur suit votre vol et vous accueille au terminal avec une pancarte à votre nom. Le règlement de l'aéroport Nice Côte d'Azur réserve aux VTC leurs propres zones de prise en charge : le parking pro au Terminal 1, la gare routière (portes A3 et A4) au Terminal 2. Un VTC n'y prend un client que sur réservation préalable : il faut donc réserver avant d'atterrir.",
    titles: (c: Ctx, min: number) => [
      `VTC ${c.from} ⇄ ${c.to} : transfert dès ${euro(min, "fr")}`,
      `VTC ${c.from} ⇄ ${c.to} dès ${euro(min, "fr")}`,
    ],
    descriptions: (c: Ctx) => {
      const tail = `~${c.durationMin} min, suivi de vol, 24 h/24. Réservez votre chauffeur privé.`;
      return [3, 2, 1].map(
        (n) =>
          `Prix fixe à la réservation : ${c.rows
            .slice(0, n)
            .map((r) => `${euro(r.price, "fr")} en ${r.classLabel}`)
            .join(", ")}. ${tail}`,
      );
    },
  },
  en: {
    priceQ: (c: Ctx) => `How much is a ${c.from} ⇄ ${c.to} car service?`,
    priceA: (c: Ctx) =>
      `The price is fixed at booking, per vehicle: ${c.rows
        .map(
          (r) =>
            `${euro(r.price, "en")} in a ${r.classLabel} (${r.maxPassengers} passengers)`,
        )
        .join(
          ", ",
        )}. Traffic does not change it. Only options you ask for (waiting, a stop on the way, oversized luggage) can adjust it, and the final price is confirmed in writing before the service.`,
    vehicleQ: (c: Ctx) =>
      `Which vehicle for ${c.from} ⇄ ${c.to}, depending on the number of passengers?`,
    vehicleA: (c: Ctx) =>
      `${byCapacity(c.rows)
        .map(
          (g) =>
            `Up to ${g[0].maxPassengers} passengers and ${g[0].maxLuggage} bags: ${listJoin(
              g.map((r) => `${r.classLabel} at ${euro(r.price, "en")}`),
              ", ",
              " or ",
            )}`,
        )
        .join(
          ". ",
        )}. For a larger group, a 16-seat Sprinter VIP is available on request.`,
    airportQ: "Where do I meet my driver at Nice Airport?",
    airportA:
      "Your driver tracks your flight and meets you in the terminal with a name sign. Nice Côte d'Azur Airport rules give private-hire drivers their own pick-up areas: the professional car park at Terminal 1, the coach station (doors A3 and A4) at Terminal 2. A private-hire driver may only pick up a passenger with a prior booking, so book before you land.",
    titles: (c: Ctx, min: number) => [
      `${c.from} ⇄ ${c.to} car service: fixed price from ${euro(min, "en")}`,
      `${c.from} ⇄ ${c.to} car service from ${euro(min, "en")}`,
      `${c.from} ⇄ ${c.to} transfer from ${euro(min, "en")}`,
    ],
    descriptions: (c: Ctx) => {
      const tail = `~${c.durationMin} min, flight tracking, 24/7. Book your private driver.`;
      return [3, 2, 1].map(
        (n) =>
          `Fixed price set at booking: ${c.rows
            .slice(0, n)
            .map((r) => `${euro(r.price, "en")} ${r.classLabel}`)
            .join(", ")}. ${tail}`,
      );
    },
  },
  ar: {
    priceQ: (c: Ctx) => `كم تكلفة سيارة مع سائق ${c.from} ⇄ ${c.to}؟`,
    priceA: (c: Ctx) =>
      `يُحدَّد السعر عند الحجز حسب المركبة: ${c.rows
        .map(
          (r) =>
            `${euro(r.price, "ar")} في ${r.classLabel} (${r.maxPassengers} ركاب)`,
        )
        .join(
          "، ",
        )}. لا تغيّره حركة المرور. وحدها الخيارات التي تطلبها (الانتظار، التوقّف في الطريق، الأمتعة غير الاعتيادية) قد تعدّله، ويُؤكَّد السعر النهائي كتابيًّا قبل الخدمة.`,
    vehicleQ: (c: Ctx) =>
      `أي مركبة أختار لرحلة ${c.from} ⇄ ${c.to} حسب عدد الركاب؟`,
    vehicleA: (c: Ctx) =>
      `${byCapacity(c.rows)
        .map(
          (g) =>
            `حتى ${g[0].maxPassengers} ركاب و${g[0].maxLuggage} حقائب: ${listJoin(
              g.map((r) => `${r.classLabel} بسعر ${euro(r.price, "ar")}`),
              "، ",
              " أو ",
            )}`,
        )
        .join(
          ". ",
        )}. للمجموعات الأكبر، تتوفّر حافلة Sprinter VIP بـ16 مقعدًا عند الطلب.`,
    airportQ: "أين ألتقي بسائقي في مطار نيس؟",
    airportA:
      "يتابع السائق رحلتك ويستقبلك في مبنى الركاب بلافتة تحمل اسمك. تخصّص أنظمة مطار نيس كوت دازور لسيارات الأجرة الخاصة مناطق استقبال محدّدة: موقف السيارات المهني في المبنى 1، ومحطة الحافلات (البوابتان A3 وA4) في المبنى 2. ولا يحقّ لها استقبال راكب إلا بحجز مسبق، لذا احجز قبل الهبوط.",
  },
} as const;

// ─── API ─────────────────────────────────────────────────────────────────────

const ctxOf = (
  from: TransferPlace,
  to: TransferPlace,
  names: { from: string; to: string },
  durationMin: number,
  rows: GeneratedPriceRow[],
): Ctx => ({
  from: names.from,
  to: names.to,
  airport: from.type === "airport" || to.type === "airport",
  durationMin,
  rows,
});

/**
 * Questions éditoriales que la FAQ générée remplace (prix « voir le tableau »,
 * choix du véhicule sans chiffres). Les autres questions éditoriales, propres
 * à chaque trajet (héliport, villas, arrêt en route…), sont gardées.
 */
const REPLACED: Record<Locale, RegExp> = {
  fr: /^(combien co[ûu]te|quel est le (tarif|prix)|quel v[ée]hicule)/i,
  en: /^(how much|what is the (rate|price)|which vehicle)/i,
  ar: /^(كم تكلفة|ما سعر|أي مركبة)/,
};

export const buildTransferFaq = (
  from: TransferPlace,
  to: TransferPlace,
  names: { from: string; to: string },
  durationMin: number,
  rows: GeneratedPriceRow[],
  locale: Locale,
  editorial: TransferFaqItem[],
): TransferFaqItem[] => {
  // Sans prix (trajet mal câblé), on ne génère rien : jamais de réponse vide.
  if (rows.length === 0) return editorial;
  const c = ctxOf(from, to, names, durationMin, rows);
  const t = TEXT[locale];
  const generated: TransferFaqItem[] = [
    { q: t.priceQ(c), a: t.priceA(c) },
    { q: t.vehicleQ(c), a: t.vehicleA(c) },
    ...(c.airport ? [{ q: t.airportQ, a: t.airportA }] : []),
  ];
  const kept = editorial.filter((item) => !REPLACED[locale].test(item.q));
  return [...generated, ...kept];
};

/**
 * Titre et description FR/EN portant le prix. L'arabe garde ses métadonnées
 * éditoriales (aucune impression AR mesurée : rien à arbitrer sur données).
 */
export const buildTransferSeo = (
  from: TransferPlace,
  to: TransferPlace,
  names: { from: string; to: string },
  durationMin: number,
  rows: GeneratedPriceRow[],
  locale: "fr" | "en",
): { title: string; description: string } | null => {
  if (rows.length === 0) return null;
  const c = ctxOf(from, to, names, durationMin, rows);
  const min = Math.min(...rows.map((r) => r.price));
  const t = TEXT[locale];
  return {
    title: fit(t.titles(c, min), MAX_TITLE),
    description: fit(t.descriptions(c), MAX_DESCRIPTION),
  };
};
